import React from 'react';
import { Link } from 'react-router-dom';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'link';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  href?: string; // 外部链接或锚点
  to?: string;   // 路由链接
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isLoading?: boolean;
}

/**
 * 通用按钮组件
 * 统一项目中的按钮样式，支持多种变体、尺寸和链接模式
 */
const Button: React.FC<ButtonProps> = ({
  children,
  className = '',
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  href,
  to,
  leftIcon,
  rightIcon,
  isLoading,
  disabled,
  ...props
}) => {
  // 基础样式
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-300 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';

  // 尺寸样式
  const sizeStyles = {
    small: 'text-xs px-4 py-2 rounded-lg',
    medium: 'text-sm px-6 py-3 rounded-xl',
    large: 'text-base px-8 py-4 rounded-xl',
  };

  // 变体样式
  const variantStyles = {
    primary: 'bg-primary text-white hover:shadow-lg hover:-translate-y-0.5 shadow-sm border border-transparent',
    secondary: 'bg-white text-gray-800 border border-gray-200 hover:border-primary hover:text-primary hover:shadow-md hover:-translate-y-0.5',
    outline: 'bg-transparent border border-primary text-primary hover:bg-primary/5',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900',
    link: 'bg-transparent text-primary hover:underline p-0 shadow-none hover:shadow-none hover:translate-y-0',
  };
  
  // 链接特有的重置样式（如果 variant 是 link，清除 padding 和 rounded）
  const linkResetStyles = variant === 'link' ? 'px-0 py-0 rounded-none' : '';

  // 组合样式
  const combinedClassName = `
    ${baseStyles}
    ${linkResetStyles || sizeStyles[size]}
    ${variantStyles[variant]}
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  // 内容渲染
  const content = (
    <>
      {isLoading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {!isLoading && leftIcon && <span className="mr-2">{leftIcon}</span>}
      {children}
      {!isLoading && rightIcon && <span className="ml-2">{rightIcon}</span>}
    </>
  );

  // 渲染为 Link
  if (to && !disabled) {
    return (
      <Link to={to} className={combinedClassName}>
        {content}
      </Link>
    );
  }

  // 渲染为 a 标签
  if (href && !disabled) {
    return (
      <a href={href} className={combinedClassName} target={href.startsWith('http') ? '_blank' : undefined} rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}>
        {content}
      </a>
    );
  }

  // 渲染为 button
  return (
    <button
      className={combinedClassName}
      disabled={disabled || isLoading}
      {...props}
    >
      {content}
    </button>
  );
};

export default Button;
